import React, { useEffect, useRef } from "react";
import { Link } from "react-scroll";
import { Github, Linkedin, Twitter } from "lucide-react";
import { Button } from "@/components/ui/button";
import gsap from "gsap";

const Hero: React.FC = () => {
  const nameRef = useRef<HTMLSpanElement>(null);
  const descriptionRef = useRef<HTMLParagraphElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            entry.target.classList.add("in-view");
          }
        });
      },
      { threshold: 0.1 }
    );

    document
      .querySelectorAll(".animate-fade-up, .animate-slide-in-right")
      .forEach((element) => {
        observer.observe(element);
      });

    // Character-by-character animation for the name
    if (nameRef.current) {
      const name = nameRef.current.textContent || "";
      nameRef.current.textContent = "";

      const chars = name.split("");
      chars.forEach((char, index) => {
        const span = document.createElement("span");
        span.textContent = char;
        span.style.opacity = "0";
        span.style.display = "inline-block";
        // Add extra space after "Chatouane"
        if (index === 9) {
          // After "Chatouane"
          const spaceSpan = document.createElement("span");
          spaceSpan.textContent = " ";
          spaceSpan.style.display = "inline-block";
          spaceSpan.style.marginRight = "0.5em"; // Add extra space
          nameRef.current?.appendChild(spaceSpan);
        }
        nameRef.current?.appendChild(span);

        gsap.to(span, {
          opacity: 1,
          duration: 0.1,
          delay: index * 0.1,
          ease: "power2.out",
        });
      });
    }

    // Line-by-line animation for the description
    if (descriptionRef.current) {
      const text = descriptionRef.current.textContent || "";
      descriptionRef.current.textContent = "";

      // Split the text into meaningful parts
      const parts = [
        "I build exceptional and accessible digital experiences for the web,",
        "specializing in",
        "React",
        "Laravel",
        "and",
        "SaaS Development.",
      ];

      parts.forEach((part, index) => {
        const span = document.createElement("span");
        span.textContent = part;
        span.style.opacity = "0";
        span.style.display = "inline-block";
        span.style.marginRight = "0.25em";
        descriptionRef.current?.appendChild(span);

        gsap.to(span, {
          opacity: 1,
          y: 0,
          duration: 0.5,
          delay: 1 + index * 0.2, // Start after name animation
          ease: "power2.out",
        });
      });
    }

    return () => observer.disconnect();
  }, []);

  return (
    <section
      id="hero"
      className="min-h-screen flex items-center justify-center relative bg-gradient-to-br from-white to-blue-50 px-4 md:px-0"
    >
      <div className="absolute inset-0 bg-grid-pattern opacity-[0.02] pointer-events-none"></div>

      <div className="container-custom grid md:grid-cols-2 gap-8 md:gap-12 items-center py-16 md:py-0">
        <div className="order-2 md:order-1 animate-fade-up text-center md:text-left">
          <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold leading-tight mb-4 text-portfolio-dark">
            Hi, I'm{" "}
            <span ref={nameRef} className="text-portfolio-blue inline-block">
              Chatouane Anas
            </span>
          </h1>

          <h2 className="text-xl md:text-2xl lg:text-3xl font-semibold mb-6 text-gray-700">
            Full-Stack Developer
          </h2>

          <p
            ref={descriptionRef}
            className="text-base md:text-lg text-gray-600 mb-8 max-w-lg mx-auto md:mx-0"
          >
            I build exceptional and accessible digital experiences for the web,
            specializing in React, Laravel, and SaaS Development.
          </p>

          <div className="flex flex-wrap gap-4 mb-8 justify-center md:justify-start">
            <Link
              to="projects"
              spy={true}
              smooth={true}
              offset={-70}
              duration={500}
            >
              <Button className="button-primary w-full sm:w-auto">
                View Projects
              </Button>
            </Link>

            <Link
              to="contact"
              spy={true}
              smooth={true}
              offset={-70}
              duration={500}
            >
              <Button className="button-secondary w-full sm:w-auto">
                Contact Me
              </Button>
            </Link>
          </div>

          <div className="flex gap-6 justify-center md:justify-start">
            <a
              href="https://github.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-600 hover:text-portfolio-blue transition-colors duration-300"
            >
              <Github size={24} />
            </a>
            <a
              href="https://linkedin.com"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-600 hover:text-portfolio-blue transition-colors duration-300"
            >
              <Linkedin size={24} />
            </a>
            <a
              href="https://x.com/AnasCh08055652"
              target="_blank"
              rel="noopener noreferrer"
              className="text-gray-600 hover:text-portfolio-blue transition-colors duration-300"
            >
              <Twitter size={24} />
            </a>
          </div>
        </div>

        <div className="order-1 md:order-2 flex justify-center animate-slide-in-right">
          <div className="relative w-48 h-48 md:w-64 md:h-64 lg:w-80 lg:h-80">
            <div className="absolute inset-0 rounded-full bg-portfolio-blue/10 backdrop-blur-sm overflow-hidden border-4 border-white shadow-xl">
              <div className="absolute inset-0 bg-gradient-to-br from-portfolio-blue/20 to-portfolio-accent/20"></div>
              <img
                src="https://media.licdn.com/dms/image/v2/D4E03AQGnqVX_ZtxNQA/profile-displayphoto-shrink_800_800/profile-displayphoto-shrink_800_800/0/1710155292449?e=1750896000&v=beta&t=rZVg8cP3QFtdiNkKNXKRqir9XAuQHuHhokd8GUZ9LaQ"
                alt="Portrait"
                className="w-full h-full object-cover"
              />
            </div>
            <div className="absolute -bottom-4 -right-4 w-24 h-24 md:w-32 md:h-32 bg-portfolio-accent/30 rounded-full blur-2xl"></div>
            <div className="absolute -top-4 -left-4 w-24 h-24 md:w-32 md:h-32 bg-portfolio-blue/20 rounded-full blur-2xl"></div>
          </div>
        </div>
      </div>
    </section>
  );
};

export default Hero;
